/**
 * Shared, client-safe plumbing for the Pal Stars arcade (9 free browser games).
 *
 * Everything in here runs in the browser and on the server (SSR) without side
 * effects at import time, so route shells can render game copy for crawlers
 * while the interactive parts hydrate later.
 *
 * Anti-cheat policy for the arcade
 * --------------------------------
 * None of the arcade games pay account currency (packs or Palsino spins), so
 * there is nothing to farm — the only thing at stake is a local best score and
 * a share card. On top of that we enforce three cheap, honest limits:
 *   1. a per-UTC-day play cap per game (stops score-spam and share-link spam),
 *   2. a minimum time per round (a human cannot answer a quiz round in 200ms),
 *   3. a hard score ceiling derived from the round count.
 * Scores that fail a guard are kept locally but flagged and never shared.
 */

export type GameId =
  | "fusion-lab"
  | "whos-that-pal"
  | "pal-draft"
  | "card-market"
  | "type-duel"
  | "pal-bracket"
  | "price-guess"
  | "pal-catch"
  | "card-match"
  | "cake-bake"
  | "build-sim";

export interface ArcadeGame {
  id: GameId;
  path: string;
  /** Short nav/card label. */
  name: string;
  /** One-sentence answer-box description (also used for AEO copy). */
  blurb: string;
  /** Primary search phrase the game page targets. */
  keyword: string;
  /** Plays allowed per UTC day (anti-cheat / anti-spam cap). */
  dailyLimit: number;
}

export const ARCADE_GAMES: ArcadeGame[] = [
  {
    id: "fusion-lab",
    path: "/fusion-lab",
    name: "Pal Fusion Lab",
    blurb:
      "Fuse any two Palworld Pals into one hybrid with a generated name, blended stat block, element pair and work suitability, then share the fusion link.",
    keyword: "palworld pal fusion generator",
    dailyLimit: 200,
  },
  {
    id: "whos-that-pal",
    path: "/whos-that-pal",
    name: "Who's That Pal?",
    blurb:
      "A timed silhouette gauntlet: name ten blacked-out Palworld Pals against the clock and share your streak.",
    keyword: "whos that palworld pal quiz",
    dailyLimit: 25,
  },
  {
    id: "pal-draft",
    path: "/pal-draft",
    name: "Pal Draft Roulette",
    blurb:
      "Roll a random legal five-Pal Palworld team under Nuzlocke-style rules and get it graded for combat, mount and base coverage.",
    keyword: "palworld random team generator",
    dailyLimit: 30,
  },
  {
    id: "card-market",
    path: "/card-market",
    name: "Card Market Flip",
    blurb:
      "A Palworld TCG trading simulator: buy and sell real card codes with virtual gold across 20 market days and beat the Pal Stars Index.",
    keyword: "palworld tcg card flipping game",
    dailyLimit: 15,
  },
  {
    id: "type-duel",
    path: "/type-duel",
    name: "Type Chart Duel",
    blurb:
      "Sixty seconds of rapid-fire Palworld element matchups — call the damage multiplier before the timer runs out.",
    keyword: "palworld type chart quiz",
    dailyLimit: 25,
  },
  {
    id: "pal-bracket",
    path: "/pal-bracket",
    name: "Best Pal Bracket",
    blurb:
      "A 16-Pal single-elimination bracket: pick a winner in every matchup and share your champion of Palpagos.",
    keyword: "best palworld pal bracket",
    dailyLimit: 20,
  },
  {
    id: "price-guess",
    path: "/price-guess",
    name: "Card Price Guesser",
    blurb:
      "Higher or lower with real Palworld TCG card codes: guess which printing carries the bigger market value and build a streak.",
    keyword: "palworld card price quiz",
    dailyLimit: 25,
  },
  {
    id: "pal-catch",
    path: "/pal-catch",
    name: "Pal Sphere Toss",
    blurb:
      "An animated Palworld catching game: Pals drift across Palpagos, you throw Pal Spheres at them before the timer ends and every catch plays its own sound.",
    keyword: "palworld catching game",
    dailyLimit: 25,
  },
  {
    id: "card-match",
    path: "/card-match",
    name: "TCG Memory Match",
    blurb:
      "A Palworld TCG memory match game: flip real card scans two at a time with animated flips and sound, and clear all eight pairs in as few moves as you can.",
    keyword: "palworld card memory match game",
    dailyLimit: 20,
  },
  {
    id: "cake-bake",
    path: "/cake-bake",
    name: "Sekhmet's Cake Kitchen",
    blurb:
      "An animated Palworld cake baking simulator: Sekhmet gathers cake ingredients, stirs the batter and times the oven — bake as many perfect cakes as you can before the clock ends.",
    keyword: "palworld cake baking game",
    dailyLimit: 25,
  },
  {
    id: "build-sim",
    path: "/build-sim",
    name: "Cattiva's Base Builder",
    blurb:
      "An animated Palworld building simulator: Cattiva hauls wood, stone and ore, then raises each blueprint block by block against the clock.",
    keyword: "palworld base building game",
    dailyLimit: 25,
  },
];

export const GAME_BY_ID: Record<GameId, ArcadeGame> = Object.fromEntries(
  ARCADE_GAMES.map((g) => [g.id, g]),
) as Record<GameId, ArcadeGame>;

/* ------------------------------- anti-cheat ------------------------------ */

/** Current UTC date as YYYY-MM-DD — every cap resets at 00:00 UTC. */
export function utcDayKey(at: Date = new Date()): string {
  return at.toISOString().slice(0, 10);
}

const KEY = (id: GameId) => `palstars.arcade.${id}`;

interface LocalGameState {
  day: string;
  plays: number;
  best: number;
}

function readState(id: GameId): LocalGameState {
  const fallback: LocalGameState = { day: utcDayKey(), plays: 0, best: 0 };
  if (typeof localStorage === "undefined") return fallback;
  try {
    const raw = localStorage.getItem(KEY(id));
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<LocalGameState>;
    const day = typeof parsed.day === "string" ? parsed.day : fallback.day;
    return {
      day,
      plays: day === utcDayKey() && typeof parsed.plays === "number" ? Math.max(0, parsed.plays) : 0,
      best: typeof parsed.best === "number" ? Math.max(0, parsed.best) : 0,
    };
  } catch {
    return fallback;
  }
}

function writeState(id: GameId, state: LocalGameState) {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(KEY(id), JSON.stringify(state));
  } catch {
    /* storage disabled — limits simply fall back to in-page counters */
  }
}

/** Plays already used today. */
export function playsToday(id: GameId): number {
  return readState(id).plays;
}

/** Plays left today for this game. */
export function playsLeft(id: GameId): number {
  return Math.max(0, GAME_BY_ID[id].dailyLimit - playsToday(id));
}

/** Records one play and returns the remaining allowance. */
export function recordPlay(id: GameId): number {
  const s = readState(id);
  const next = { ...s, day: utcDayKey(), plays: s.plays + 1 };
  writeState(id, next);
  return Math.max(0, GAME_BY_ID[id].dailyLimit - next.plays);
}

/** Local best score (never authoritative, never rewarded). */
export function bestScore(id: GameId): number {
  return readState(id).best;
}

/** Stores a new best score when it passes the sanity guards. */
export function submitScore(id: GameId, score: number): { best: number; accepted: boolean } {
  const s = readState(id);
  const accepted = Number.isFinite(score) && score >= 0 && score <= 10_000;
  const best = accepted ? Math.max(s.best, Math.floor(score)) : s.best;
  writeState(id, { ...s, day: utcDayKey(), best });
  // Every accepted run also counts towards the current weekly tournament board.
  if (accepted) recordWeeklyScore(id, Math.floor(score));
  return { best, accepted };
}

/* --------------------------- weekly tournaments -------------------------- */

/** ISO-week id, e.g. "2026-W35" — the tournament week rolls over Monday 00:00 UTC. */
export function isoWeek(at: Date = new Date()): string {
  const d = new Date(Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = Date.UTC(d.getUTCFullYear(), 0, 1);
  const week = Math.ceil(((d.getTime() - yearStart) / 86_400_000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

/** Milliseconds until the current tournament week closes (Monday 00:00 UTC). */
export function msUntilWeekEnd(at: Date = new Date()): number {
  const d = new Date(at);
  const day = d.getUTCDay() || 7;
  const end = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + (8 - day), 0, 0, 0);
  return Math.max(0, end - at.getTime());
}

/** The three games featured in a given tournament week (deterministic rotation). */
export function weeklyLineup(week: string = isoWeek()): ArcadeGame[] {
  const start = hash(week) % ARCADE_GAMES.length;
  return [0, 1, 2].map((i) => ARCADE_GAMES[(start + i * 3) % ARCADE_GAMES.length]!);
}

const WEEK_KEY = (week: string) => `palstars.tournament.${week}`;

/** This week's best score per game, in this browser. */
export function weeklyScores(week: string = isoWeek()): Partial<Record<GameId, number>> {
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(WEEK_KEY(week));
    return raw ? (JSON.parse(raw) as Partial<Record<GameId, number>>) : {};
  } catch {
    return {};
  }
}

/** Records a run against the current tournament week (best score per game). */
export function recordWeeklyScore(id: GameId, score: number): void {
  if (typeof localStorage === "undefined") return;
  const week = isoWeek();
  const board = weeklyScores(week);
  if ((board[id] ?? 0) >= score) return;
  board[id] = score;
  try {
    localStorage.setItem(WEEK_KEY(week), JSON.stringify(board));
  } catch {
    /* storage disabled — the tournament board just isn't kept */
  }
}

/**
 * Tournament points: each featured game contributes its weekly best, normalised
 * so no single game can dominate the table (cap of 100 points per game).
 */
export function weeklyPoints(week: string = isoWeek()): { total: number; perGame: { game: ArcadeGame; score: number; points: number }[] } {
  const board = weeklyScores(week);
  const perGame = weeklyLineup(week).map((game) => {
    const score = board[game.id] ?? 0;
    return { game, score, points: Math.min(100, score) };
  });
  return { total: perGame.reduce((n, g) => n + g.points, 0), perGame };
}


/**
 * Score plausibility guard. A run is rejected when it is faster than humanly
 * possible or scores more than the round count allows.
 */
export function isPlausibleRun(opts: {
  score: number;
  rounds: number;
  elapsedMs: number;
  minMsPerRound?: number;
  maxPerRound?: number;
}): boolean {
  const { score, rounds, elapsedMs, minMsPerRound = 250, maxPerRound = 1 } = opts;
  if (!Number.isFinite(score) || score < 0) return false;
  if (rounds < 0 || score > rounds * maxPerRound) return false;
  if (rounds > 0 && elapsedMs < rounds * minMsPerRound) return false;
  return true;
}

/* --------------------------- share & referrals --------------------------- */

const SITE = "https://palstars.com";
const REFERRAL_KEY = "palstars.referral";

/** Stores the ?ref= code a visitor arrived with, so their own shares credit it. */
export function captureReferral(search: string): string | null {
  const code = new URLSearchParams(search).get("ref");
  if (!code) return null;
  const clean = code.replace(/[^a-z0-9-]/gi, "").slice(0, 32);
  if (!clean) return null;
  try {
    localStorage.setItem(REFERRAL_KEY, clean);
  } catch {
    /* ignore */
  }
  return clean;
}

/** The referral code to attach to outgoing share links. */
export function referralCode(id: GameId): string {
  try {
    const stored = localStorage.getItem(REFERRAL_KEY);
    if (stored) return stored;
  } catch {
    /* ignore */
  }
  return `arcade-${id}`;
}

/**
 * Canonical share URL for a game result: always the indexable page URL plus a
 * referral parameter (canonical tags keep the parameterised URL out of the
 * index, so shares never create duplicate pages).
 */
export function shareLink(id: GameId, params: Record<string, string> = {}): string {
  const game = GAME_BY_ID[id];
  const qs = new URLSearchParams({ ...params, ref: referralCode(id) });
  return `${SITE}${game.path}?${qs.toString()}`;
}

/** Copies text to the clipboard, falling back to a hidden textarea. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const el = document.createElement("textarea");
      el.value = text;
      el.setAttribute("readonly", "");
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(el);
      return ok;
    } catch {
      return false;
    }
  }
}

/** Deterministic 32-bit hash — used for daily seeds and fusion maths. */
export function hash(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Small mulberry32 PRNG so a seeded run is reproducible and shareable. */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* ---------------------------------- audio -------------------------------- */

/**
 * Plays one arcade cue through the shared sound engine, honouring the sitewide
 * mute/volume preference set by <SoundControls />.
 */
export async function cue(name: "click" | "flip" | "win" | "lose" | "chime" | "jackpot") {
  try {
    const { playSfx, loadSfxPrefs, unlockAudio } = await import("./sound");
    const { volume, muted } = loadSfxPrefs();
    // Cues only fire from real user gestures (start button, card tap, catch
    // click…), so unlocking here is autoplay-compliant and means the first
    // interaction already has sound instead of staying silent until the
    // volume slider is touched.
    unlockAudio();
    playSfx(name, volume, muted);
  } catch {
    /* audio unavailable — games stay fully playable */
  }
}
